import { useEffect, useState } from 'react';
import { Modal } from '../../../components/modal/modal';
import { Field } from '../../../components/field/field';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import { Spinner } from '../../../components/spinner/spinner';
import { useAdminFormState } from '../hooks/use-admin-form-state';
import { useAdminOperations } from '../hooks/use-admin-operations';
import { toast } from '../../../util/toasts';
import { PasswordRequirements } from '../../../clients/quipli-service/admin/admin';

interface CreateAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAdminModal = ({ isOpen, onClose, onSuccess }: CreateAdminModalProps) => {
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements | null>(null);
  const { createUser, fetchPasswordRequirements, isCreating } = useAdminOperations();

  const {
    // Form state
    adminUsername,
    adminFullName,
    adminEmail,
    newPassword,
    confirmNewPassword,
    errorMessage,
    isDisabled,

    // Form actions
    setUsername,
    setFullname,
    setEmail,
    setNewPassword,
    setConfirmNewPassword,

    // Validation functions
    checkConfirmPassword,
  } = useAdminFormState({
    type: 'create',
    passwordRequirements: passwordRequirements || undefined,
  });

  // Load password requirements on mount
  useEffect(() => {
    if (isOpen) {
      fetchPasswordRequirements().then(setPasswordRequirements);
    }
  }, [isOpen]);

  const handleCreateUser = async () => {
    if (!checkConfirmPassword()) {
      toast('Error password and confirm password should be the same', 'error');
      return;
    }

    const newAdmin = await createUser({
      adminUsername,
      adminEmail,
      adminFullName,
      adminPassword: newPassword,
    });
    if (newAdmin) {
      onSuccess();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal title="Add new user" isOpen={isOpen} close={onClose}>
      <div className="w-full">
        <Field
          className="w-full"
          type="text"
          label="Full name"
          value={adminFullName}
          onChange={(fullname: string) => {
            setFullname(fullname);
          }}
          isRequired={true}
          dataCy="admin-fullname"
        />
        <Field
          className="w-full"
          type="text"
          label="Primary email"
          value={adminEmail}
          onChange={(email: string) => {
            setEmail(email);
          }}
          isRequired={true}
          dataCy="admin-email"
        />
        <Field
          className="w-full"
          type="text"
          label="User name"
          value={adminUsername}
          onChange={(username: string) => {
            setUsername(username);
          }}
          isRequired={true}
          dataCy="admin-username"
        />
        <Field
          className="w-full"
          type="password"
          label="Password"
          value={newPassword}
          onChange={(password: string) => {
            setNewPassword(password);
          }}
          isRequired={true}
          dataCy="admin-password"
        />
        <Field
          className="w-full"
          type="password"
          label="Confirm Password"
          value={confirmNewPassword}
          onChange={(confirmPassword: string) => {
            setConfirmNewPassword(confirmPassword);
          }}
          isRequired={true}
          dataCy="admin-confirm-password"
        />
        {errorMessage && <div className="text-[var(--error-500)]">{errorMessage}</div>}
      </div>

      <div className="dialog-actions !px-0 !pb-0">
        <div className="flex flex-col sm:flex-row actionsContainer">
          {isCreating ? (
            <Spinner />
          ) : (
            <>
              <Button
                className="button button-block button-secondary mr-2 mb-2"
                onClick={onClose}
                dataCy="admin-cancel-button"
              >
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Cancel
                </Typography>
              </Button>

              <Button
                className="button button-yellow button-primary mb-2"
                onClick={handleCreateUser}
                disabled={isDisabled}
                dataCy="admin-add-button"
              >
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Add
                </Typography>
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
